import { useState, useRef, useEffect, useCallback } from "react";
import {
  ArrowRight, ChevronRight, X, Search,
  Box, Palette, Printer, BookOpen, Package, Globe, Gift, Image,
  Star, Quote, ChevronLeft, Award, ZoomIn, TrendingUp,
} from "lucide-react";
import "./Portfoliopage.css";

import { useNavigate } from "react-router-dom";



/* ─── Asset imports ─────────────────────────────────────────── */
import largeFormatImg from "../assets/images/largeFormat.jpeg";
import bags1          from "../assets/images/bags1.jpg";
import bags2          from "../assets/images/bags2.jpeg";
import boxes          from "../assets/images/boxes.jpeg";
import threeD1        from "../assets/images/threeD2.jpeg";
import threeD2        from "../assets/images/threeD.jpg";
import threeD3        from "../assets/images/threeD1.jpg";
import brand1         from "../assets/images/brand1.jpeg";
import brand2         from "../assets/images/brand2.jpeg";
import brand3         from "../assets/images/brand3.jpeg";
import brand4         from "../assets/images/brand4.jpg";
import brand5         from "../assets/images/brand5.jpg";
import brochure       from "../assets/images/brochure.jpg";
import flyers         from "../assets/images/flyers.jpg";
import gift1          from "../assets/images/gift1.jpg";
import bdCard         from "../assets/images/bdCard.jpg";
import tshirt         from "../assets/images/tshirt.jpg";
import tshirt2        from "../assets/images/tshirtsPrinting.jpg";
import wristband      from "../assets/images/wristband.jpg";
import reflectors     from "../assets/images/reflectors.jpg";
import lanyard        from "../assets/images/lanyard.jpg";
import caps           from "../assets/images/Caps.jpg";
import book1          from "../assets/images/book1.jpeg";
import book2          from "../assets/images/book2.jpeg";
import book3          from "../assets/images/book3.jpeg";
import book4          from "../assets/images/book4.jpg";

import user from '../assets/images/user.jpg'

/* ─── useInView ─────────────────────────────────────────────── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ─── Animated counter ──────────────────────────────────────── */
function Counter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const [ref, vis] = useInView(0.3);
  const started = useRef(false);
  useEffect(() => {
    if (!vis || started.current) return;
    started.current = true;
    const steps = 60;
    let step = 0;
    const t = setInterval(() => {
      step++;
      setCount(Math.round((target / steps) * step));
      if (step >= steps) clearInterval(t);
    }, 1800 / steps);
    return () => clearInterval(t);
  }, [vis, target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Data ───────────────────────────────────────────────────── */
const FILTERS = [
  "All", "3D Signage", "Branding", "Printing",
  "Book Printing", "Packaging", "Web Design", "Merch", "Large Format",
];

const PROJECTS = [
  /* tall = spans 2 rows, wide = spans 2 cols */
  { id:1,  cat:"3D Signage",    title:"Corporate Reception Sign",    desc:"Illuminated 3D acrylic letters for a Nairobi HQ lobby.", tall:true,  img:threeD3  },
  { id:2,  cat:"Branding",      title:"Restaurant Brand Identity",   desc:"Full brand system — logo, menus, uniforms and signage.", img:brand1 },
  { id:3,  cat:"Branding",      title:"Brand Guidelines System",     desc:"Comprehensive visual identity rulebook and assets.",      img:brand2 },
  { id:4,  cat:"3D Signage",    title:"Illuminated Shop Front",      desc:"LED channel letters on aluminium cladding.",   wide:true, img:threeD2  },
  { id:5,  cat:"Packaging",     title:"Luxury Skincare Packaging",   desc:"Premium bags, boxes and labels for a beauty brand.", tall:true, img:bags2 },
  { id:6,  cat:"Printing",      title:"Business Cards & Stationery", desc:"Spot-UV cards, letterheads and envelopes.", img:bdCard },
  { id:7,  cat:"Printing",      title:"Brochure & Flyer Suite",      desc:"Full-colour brochures and event flyers.",    img:flyers },
  { id:8,  cat:"Book Printing", title:"School Annual Magazine",      desc:"64-page full-colour A4 magazine, saddle-stitched.", wide:true, img:book1 },
  { id:9,  cat:"Book Printing", title:"Church Hymnal",               desc:"320-page perfect-bound hymnal, 500 copies.",  img:book2 },
  { id:10, cat:"Merch",         title:"Corporate T-Shirt Print",     desc:"Screen-printed event tees in a full size range.", img:tshirt },
  { id:11, cat:"Merch",         title:"Branded Lanyards & Caps",     desc:"Sublimation lanyards and embroidered caps.",  img:caps },
  { id:12, cat:"Packaging",     title:"Paper Bags & Boxes",          desc:"Custom-shaped bags and folding boxes.", img:boxes },
  { id:13, cat:"Large Format",  title:"Banner & Roll-up Stands",     desc:"Event roll-ups and large outdoor banners.", img:largeFormatImg },
  { id:14, cat:"Merch",         title:"Wristbands & Reflectors",     desc:"Silicone wristbands and hi-vis reflectors.", img:wristband },
  { id:15, cat:"Branding",      title:"Logo & Identity System",      desc:"Bespoke mark with colour and usage guidelines.", img:brand3 },
  { id:16, cat:"Book Printing", title:"Company Profile Book",        desc:"Premium perfect-bound profile for tenders.", img:book3 },
];

const CASE_STUDIES = [
  {
    img: threeD1,
    tag: "3D Signage",
    title: "Savanna Finance — HQ Rebrand & Signage",
    challenge: "A finance firm needed physical presence to match a new premium digital brand.",
    solution: "Fabricated and installed illuminated reception signage, external fascia letters, directional system and boardroom branding.",
    result: "40% increase in walk-in confidence. Consistent client feedback on 'world-class' premises.",
    metrics: [{ n:"40%", l:"Walk-in confidence" }, { n:"3 weeks", l:"Design to install" }, { n:"12", l:"Signage pieces" }],
  },
  {
    img: brand5,
    tag: "Full Rebrand",
    title: "Bloom Bakery — Complete Brand System",
    challenge: "An artisan bakery needed a brand strong enough to open in Nairobi malls.",
    solution: "Logo, brand guidelines, packaging (bags, boxes, labels), staff uniforms, social media kit and Shopify website.",
    result: "Two new mall locations opened within six months. Social following grew 3× in 90 days.",
    metrics: [{ n:"3×", l:"Social growth" }, { n:"2", l:"New locations" }, { n:"90 days", l:"Full rollout" }],
  },
  {
    img: book4,
    tag: "Book Printing",
    title: "Grace Chapel — Annual Hymnal Print Run",
    challenge: "500 copies of a 320-page hymnal needed for a Sunday launch with zero margin for error.",
    solution: "Typesetting review, colour-managed printing, perfect binding and shrink-wrap delivery — fully managed end to end.",
    result: "All 500 copies delivered 3 days ahead of deadline. Zero defects reported.",
    metrics: [{ n:"500", l:"Books delivered" }, { n:"10 days", l:"Turnaround" }, { n:"0", l:"Defects" }],
  },
];

const CAROUSEL_IMGS = [threeD3, brand1, bags2, book1, tshirt, largeFormatImg, brochure, caps];

const TSTS = [
  { name:"Dr. Peter Hayombe",  role:"Founder Simba Education Consultancy",     service:"3D Signage",   av:user, review:"Our new shopfront signage is breathtaking. Customers come specifically to photograph it. Bestmark delivered quality we didn't think was possible in Kenya." },
  { name:"Dr. Mbare",  role:"CEO Afrowist", service:"Full Rebrand",  av:user, review:"Complete rebrand — logo, website, print, merch — executed perfectly. Every touchpoint was consistent and premium." },
  { name:"Dr. Arshley",  role:"Founder, Green Apple",   service:"Packaging",    av:user, review:"The packaging transformed our products into a luxury brand overnight. We started getting stocked in premium supermarkets within two months." },
];

const CLIENT_LOGOS = [
  "Savanna Retail","Horizon Group","Bloom Bakery","Grace Chapel",
  "Nakuru Foods","Savvy Motors","Kenya NGO Alliance","Prestige Law",
  "Orbit Schools","FlexLogix KE","Summit Hotels","Crafted Brew",
];

const BTS = [
  { src:brochure,      alt:"Print production",    cls:"pf-wide" },
  { src:threeD2,       alt:"Signage installation", cls:"pf-tall" },
  { src:bags1,         alt:"Packaging production"              },
  { src:brand4,        alt:"Design process"                    },
  { src:tshirt2,       alt:"Merch production",    cls:"pf-wide" },
  { src:book2,         alt:"Book printing"                     },
  { src:gift1,         alt:"Gift set assembly"                 },
];

/* ─── Shared UI ─────────────────────────────────────────────── */
function Pill({ children }) {
  return <div className="pf-pill"><span className="pf-dot" />{children}</div>;
}
function GD() { return <div className="pf-gd" />; }

/* ─── Lightbox ──────────────────────────────────────────────── */
function Lightbox({ project, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="pf-lb" onClick={onClose} role="dialog" aria-modal="true">
      <button className="pf-lb__close" onClick={onClose} aria-label="Close"><X size={20} /></button>
      <img
        className="pf-lb__img"
        src={project.img}
        alt={project.title}
        onClick={e => e.stopPropagation()}
      />
      <div className="pf-lb__meta">
        <div className="pf-lb__title">{project.title}</div>
        <div className="pf-lb__cat">{project.cat}</div>
      </div>
    </div>
  );
}

/* ─── Project card ──────────────────────────────────────────── */
function ProjectCard({ project, index, vis, onOpen }) {
  const cls = [
    "pf-proj",
    `pf-fade d${Math.min((index % 6) + 1, 6)}`,
    vis ? "in" : "",
    project.tall ? "pf-proj--tall" : "",
    project.wide ? "pf-proj--wide" : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={cls}
      onClick={() => onOpen(project)}
      role="button"
      tabIndex={0}
      aria-label={`View ${project.title}`}
      onKeyDown={e => { if (e.key === "Enter") onOpen(project); }}
    >
      <img className="pf-proj__img" src={project.img} alt={project.title} loading="lazy" />
      <div className="pf-proj__ov">
        <div className="pf-proj__cat">{project.cat}</div>
        <div className="pf-proj__title">{project.title}</div>
        <p className="pf-proj__desc">{project.desc}</p>
        <div className="pf-proj__zoom"><ZoomIn size={16} /></div>
      </div>
    </div>
  );
}

/* ─── HERO ───────────────────────────────────────────────────── */
function Hero() {
  const [ref, vis] = useInView(0.05);
  const navigate   = useNavigate();
  return (
    <section ref={ref} className="pf-hero" aria-label="Portfolio hero">
      <div className="pf-hero__grid" />
      <div className="pf-hero__orb pf-hero__orb--a" />
      <div className="pf-hero__orb pf-hero__orb--b" />
      <div className="pf-hero__shape pf-hero__shape--sq" />
      <div className="pf-hero__shape pf-hero__shape--dot" />
      <div className="pf-hero__shape pf-hero__shape--sm" />

      <div className="pf-hero__inner">
        <div className={`pf-fade${vis ? " in" : ""}`}><Pill>Our Portfolio</Pill></div>
        <h1 className={`pf-hero__h1 pf-fade d1${vis ? " in" : ""}`}>
          Turning Creative{" "}
          <em className="pf-italic">Ideas</em>{" "}
          Into Real Results
        </h1>
        <p className={`pf-hero__sub pf-fade d2${vis ? " in" : ""}`}>
          Explore projects across branding, signage, printing, packaging,
          book printing, web design and promotional merchandise.
        </p>
        <div className={`pf-hero__btns pf-fade d3${vis ? " in" : ""}`}>
          <button className="ft-btn-r ft-btn--lg" onClick={() => navigate("/contact")}>
            Get Free Quote <ArrowRight size={16} />
          </button>
          <button className="ft-btn-g ft-btn--lg" onClick={() => navigate("/contact")}>
            Contact Us <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─── STATS ─────────────────────────────────────────────────── */
function Stats() {
  const [ref, vis] = useInView(0.1);
  return (
    <div ref={ref} className="pf-stats-bar">
      {[
        { n:100, sx:"+", l:"Completed Projects" },
        { n:50,  sx:"+", l:"Happy Clients"      },
        { n:8,   sx:"+", l:"Service Categories" },
        { n:98,  sx:"%", l:"Client Satisfaction"},
      ].map((s, i) => (
        <div key={s.l} className={`pf-stat pf-fade d${i+1}${vis ? " in" : ""}`}>
          <div className="pf-stat__n"><Counter target={s.n} suffix={s.sx} /></div>
          <div className="pf-stat__l">{s.l}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── PROJECT GRID ───────────────────────────────────────────── */
function ProjectGrid() {
  const [ref, vis]      = useInView(0.05);
  const [active, setActive] = useState("All");
  const [query,  setQuery]  = useState("");
  const [lb,     setLb]     = useState(null);
  const closeLb = useCallback(() => setLb(null), []);

  const filtered = PROJECTS.filter(p => {
    const matchCat = active === "All" || p.cat === active;
    const matchQ   = !query || p.title.toLowerCase().includes(query.toLowerCase()) || p.cat.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQ;
  });

  return (
    <section className="pf-sec" ref={ref} aria-label="Project gallery">
      <div className="pf-inner">
        <div className={`pf-fade${vis ? " in" : ""}`}><Pill>Featured Work</Pill></div>
        <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>Our Completed Projects</h2>

        {/* Search */}
        <div className={`pf-search pf-fade d2${vis ? " in" : ""}`}>
          <Search className="pf-search__icon" size={16} />
          <input
            className="pf-search__input"
            type="search"
            placeholder="Search projects…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search projects"
          />
        </div>

        {/* Filter pills */}
        <div className={`pf-filters pf-fade d3${vis ? " in" : ""}`}>
          {FILTERS.map(f => (
            <button
              key={f}
              className={`pf-filter${active === f ? " active" : ""}`}
              onClick={() => setActive(f)}
              aria-pressed={active === f}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Bento grid */}
        <div className="pf-proj-grid">
          {filtered.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} vis={vis} onOpen={setLb} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="pf-empty">No projects found for "{query}" in "{active}".</div>
        )}
      </div>

      {lb && <Lightbox project={lb} onClose={closeLb} />}
    </section>
  );
}

/* ─── CASE STUDIES ───────────────────────────────────────────── */
function CaseStudies() {
  const [ref, vis] = useInView(0.06);
  return (
    <section className="pf-sec pf-sec--surface" ref={ref} aria-label="Case studies">
      <div className="pf-inner">
        <div className="pf-section-head--center">
          <div className={`pf-fade${vis ? " in" : ""}`} style={{ display:"flex",justifyContent:"center" }}><Pill>Deep Dives</Pill></div>
          <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>Case Studies</h2>
          <p className={`pf-sub pf-fade d2${vis ? " in" : ""}`}>
            Three projects examined in detail — challenge, solution and results.
          </p>
        </div>

        <div className="pf-cs-list">
          {CASE_STUDIES.map((cs, i) => (
            <div key={cs.title} className={`pf-cs pf-scl d${i+1}${vis ? " in" : ""}`}>
              <div className="pf-cs__img-wrap">
                <img className="pf-cs__img" src={cs.img} alt={cs.title} loading="lazy" />
              </div>
              <div className="pf-cs__body">
                <div className="pf-cs__tag">{cs.tag}</div>
                <div className="pf-cs__title">{cs.title}</div>
                <div className="pf-cs__row">
                  {[["Challenge", cs.challenge], ["Solution", cs.solution], ["Result", cs.result]].map(([label, val]) => (
                    <div key={label}>
                      <div className="pf-cs__label">{label}</div>
                      <div className="pf-cs__val">{val}</div>
                    </div>
                  ))}
                </div>
                <div className="pf-cs__metrics">
                  {cs.metrics.map(m => (
                    <div className="pf-cs__metric" key={m.l}>
                      <div className="pf-cs__metric-n">{m.n}</div>
                      <div className="pf-cs__metric-l">{m.l}</div>
                    </div>
                  ))}
                </div>
                <button className="pf-btn-r" style={{ marginTop:20 }}>
                  Read Case Study <ArrowRight size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CAROUSEL ───────────────────────────────────────────────── */
function Carousel() {
  const [ref, vis] = useInView(0.08);
  const [idx, setIdx] = useState(0);
  const visible = 3;
  const max = CAROUSEL_IMGS.length - visible;
  const prev = () => setIdx(i => Math.max(0, i - 1));
  const next = () => setIdx(i => Math.min(max, i + 1));

  return (
    <section className="pf-sec" ref={ref} aria-label="Project showcase">
      <div className="pf-inner">
        <div className="pf-carousel__head">
          <div>
            <div className={`pf-fade${vis ? " in" : ""}`}><Pill>Project Showcase</Pill></div>
            <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>Browse Our Work</h2>
          </div>
          <div className="pf-carousel__nav">
            <button className="pf-car-btn" onClick={prev} aria-label="Previous" disabled={idx === 0} style={{ opacity: idx===0 ? .4 : 1 }}>
              <ChevronLeft size={18} />
            </button>
            <button className="pf-car-btn" onClick={next} aria-label="Next" disabled={idx >= max} style={{ opacity: idx>=max ? .4 : 1 }}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="pf-carousel">
          <div
            className="pf-carousel__track"
            style={{ transform: `translateX(calc(-${idx * (100/visible)}% - ${idx * 16/visible}px))` }}
          >
            {CAROUSEL_IMGS.map((src, i) => (
              <div key={i} className="pf-carousel__item">
                <img src={src} alt={`Project ${i+1}`} loading="lazy" />
                <div className="pf-carousel__ov" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ───────────────────────────────────────────── */
function Testimonials() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="pf-sec pf-sec--surface" ref={ref} aria-label="Testimonials">
      <div className="pf-inner">
        <div className="pf-section-head--center">
          <div className={`pf-fade${vis ? " in" : ""}`} style={{ display:"flex",justifyContent:"center" }}><Pill>Client Stories</Pill></div>
          <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>What Our Clients Say</h2>
        </div>

        <div className="pf-tst-grid">
          {TSTS.map((t, i) => (
            <div key={t.name} className={`pf-tst pf-scl d${i+1}${vis ? " in" : ""}`}>
              <Quote size={22} color="#E31E24" className="pf-tst__quote" />
              <div className="pf-tst__stars">
                {[...Array(5)].map((_, si) => <Star key={si} size={12} fill="#E31E24" color="#E31E24" />)}
              </div>
              <div className="pf-tst__service">{t.service}</div>
              <p className="pf-tst__review">"{t.review}"</p>
              <div className="pf-tst__foot">
                <img className="pf-tst__av" src={t.av} alt={t.name} loading="lazy" />
                <div>
                  <div className="pf-tst__name">{t.name}</div>
                  <div className="pf-tst__role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CLIENT LOGOS ───────────────────────────────────────────── */
function ClientLogos() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="pf-sec" ref={ref} aria-label="Trusted clients">
      <div className="pf-inner">
        <div className="pf-section-head--center">
          <div className={`pf-fade${vis ? " in" : ""}`} style={{ display:"flex",justifyContent:"center" }}><Pill>Trusted By</Pill></div>
          <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>Businesses Across Kenya</h2>
        </div>
        <div className="pf-logos">
          {CLIENT_LOGOS.map((name, i) => (
            <div key={name} className={`pf-logo pf-fade d${Math.min(i%6+1,6)}${vis ? " in" : ""}`}>
              <span>{name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── BEHIND THE SCENES ──────────────────────────────────────── */
function BehindScenes() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="pf-sec pf-sec--surface" ref={ref} aria-label="Behind the scenes">
      <div className="pf-inner">
        <div className="pf-section-head--center">
          <div className={`pf-fade${vis ? " in" : ""}`} style={{ display:"flex",justifyContent:"center" }}><Pill>Behind the Scenes</Pill></div>
          <h2 className={`pf-h2 pf-fade d1${vis ? " in" : ""}`}>How the Work Gets Made</h2>
        </div>
        <div className="pf-bts">
          {BTS.map((item, i) => (
            <div
              key={i}
              className={`pf-bts__item pf-scl d${Math.min(i+1,6)}${vis ? " in" : ""}${item.cls ? " "+item.cls : ""}`}
            >
              <img src={item.src} alt={item.alt} loading="lazy" />
              <div className="pf-bts__ov" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ────────────────────────────────────────────────────── */
function CTA() {
  const [ref, vis] = useInView(0.1);
  return (
    <section className="pf-sec" ref={ref} aria-label="Call to action">
      <div className="pf-inner">
        <div className={`pf-cta pf-scl${vis ? " in" : ""}`}>
          <div className="pf-cta__orb" />
          <div className="pf-cta__shape pf-cta__shape--sq" />
          <div className="pf-cta__shape pf-cta__shape--dot" />
          <div className="pf-cta__body">
            <div className={`pf-fade${vis ? " in" : ""}`} style={{ display:"flex",justifyContent:"center",marginBottom:20 }}>
              <Pill>Let's Build Together</Pill>
            </div>
            <h2 className={`pf-cta__h2 pf-fade d1${vis ? " in" : ""}`}>
              Ready To Start<br />
              <em className="pf-italic">Your Project?</em>
            </h2>
            <p className={`pf-cta__sub pf-fade d2${vis ? " in" : ""}`}>
              Let's create something extraordinary for your brand.
              We're just a call or message away.
            </p>
            <div className={`pf-cta__btns pf-fade d3${vis ? " in" : ""}`}>
              <button className="pf-btn-r pf-btn--lg">Get Free Quote <ArrowRight size={16} /></button>
              <button className="pf-btn-g pf-btn--lg">Call 0794 437 498</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Root ───────────────────────────────────────────────────── */
export default function PortfolioPage() {
  return (
    <>
      <a href="#pf-main" className="pf-skip">Skip to main content</a>
      <main id="pf-main">
        <Hero />
        <Stats />
        <GD />
        <ProjectGrid />
        <GD />
        {/* <CaseStudies /> */}
        <GD />
        <Carousel />
        <GD />
        <Testimonials />
        <GD />
        <ClientLogos />
        <GD />
        <BehindScenes />
        <GD />
        <CTA />
      </main>
    </>
  );
}